import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Autocomplete, Fade } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotVersion } from "@/store/ses/sesRobotSlice";
import { changeBooleanValuesWebConnector, changeConnectorName, changeStringValuesWebConnector, changeTypeWebConnector } from "@/store/ses/sesConnectorSlice";
import { getTranslateList, selectTranslateList } from "@/store/langSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { LANG, SES } from "@/constants/accessRights";
import { WEB_CONNECTOR_TYPES_LIST } from "@/constants/robotConfigLists";
import { NoOptionText, RenderOption, CustomCheckbox, CustomMenuItem, CustomTextField, H3 } from "@/ui";
import Server from "../Server/Server";
import FieldValueBlock from "../FieldValueBlock/FieldValueBlock";
import { TTypeWeb } from "@/types/ses/sesConnectorTypes";
import { IWebProps } from "./Web.props";
import styles from "./Web.module.scss";

const Web = ({ selectId, channel, settings, connectorName, changeFlgHandler }: IWebProps): JSX.Element => {
	const [inputConnectorName, setInputConnectorName] = useState<string>(""); // название коннектора
	const [selectWidgetType, setSelectWidgetType] = useState<TTypeWeb>("chat"); // тип виджета
	const [inputDomain, setInputDomain] = useState<string>(""); // домен
	const [inputToken, setInputToken] = useState<string>(""); // токен
	const [inputColor, setInputColor] = useState<string>(""); // акцентный цвет
	const [inputHeader, setInputHeader] = useState<string>(""); // заголовок
	const [inputInitText, setInputInitText] = useState<string>(""); // приветственное сообщение
	const [inputLanguage, setInputLanguage] = useState<string>(""); // язык
	const [checkAudioAnswers, setCheckAudioAnswers] = useState<boolean>(false); // аудио-ответы
	const [checkSendFile, setCheckSendFile] = useState<boolean>(false); // отправка файлов
	const [checkMicrophone, setCheckMicrophone] = useState<boolean>(false); // отправка аудио-сообщений
	const [checkAudioCall, setCheckAudioCall] = useState<boolean>(false); // аудио-звонок

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const translateList = useAppSelector(selectTranslateList); // store - список языков переводов

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за списком переводов
	useEffect(() => {
		if (translateList.data.length === 0) {
			isAccess(LANG.LIST) && dispatch(getTranslateList()); // получаем список словарей для перевода текста
		}
	}, [translateList.data]);

	const isDisabled = useMemo(() => {
		return !isAccess(SES.CONNECTOR_MODIFY) || activeRobotVersion === "backup";
	}, [activeRobotVersion]);

	// следим за каналом, названием коннектора и заполняем данные
	useEffect(() => {
		setInputConnectorName(connectorName);
		setSelectWidgetType(settings.type);
		setInputDomain(settings.domain);
		setInputToken(settings.token);
		setInputColor(settings.color);
		setInputHeader(settings.header);
		setInputInitText(settings.inittext);
		setInputLanguage(settings.language);
		if (settings.type === "chat") {
			setCheckAudioAnswers(settings.audioAnswers);
			setCheckSendFile(settings.sendFile);
			setCheckMicrophone(settings.microphone);
			setCheckAudioCall(settings.audioCall);
		}
	}, [selectId, channel, connectorName, settings]);

	// обработчик изменения названия коннектора
	const connectorNameChangeHandler = (): void => {
		if (connectorName === inputConnectorName) return;
		dispatch(changeConnectorName({ newName: inputConnectorName }));
		changeFlgHandler("name");
	};

	// обработчик изменения типа виджета
	const widgetTypeChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = e.target.value as TTypeWeb;
		setSelectWidgetType(value);
		dispatch(changeTypeWebConnector({ type: value }));
		changeFlgHandler("params");
	};

	// обработчик изменения домена
	const domainChangeHandler = (): void => {
		if (settings.domain === inputDomain) return;
		dispatch(changeStringValuesWebConnector({ channel, field: "domain", value: inputDomain }));
		changeFlgHandler("params");
	};

	// обработчик изменения токена
	const tokenChangeHandler = (): void => {
		if (settings.token === inputToken) return;
		dispatch(changeStringValuesWebConnector({ channel, field: "token", value: inputToken }));
		changeFlgHandler("params");
	};

	// обработчик изменения акцентного цвета
	const colorChangeHandler = (): void => {
		if (settings.color === inputColor) return;
		dispatch(changeStringValuesWebConnector({ channel, field: "color", value: inputColor }));
		changeFlgHandler("params");
	};

	// обработчик изменения заголовка
	const headerChangeHandler = (): void => {
		if (settings.header === inputHeader) return;
		dispatch(changeStringValuesWebConnector({ channel, field: "header", value: inputHeader }));
		changeFlgHandler("params");
	};

	// обработчик изменения приветственного сообщения
	const initTextChangeHandler = (): void => {
		if (settings.inittext === inputInitText) return;
		dispatch(changeStringValuesWebConnector({ channel, field: "inittext", value: inputInitText }));
		changeFlgHandler("params");
	};

	// обработчик изменения языка
	const languageChangeHandler = (): void => {
		if (settings.language === inputLanguage) return;
		dispatch(changeStringValuesWebConnector({ channel, field: "language", value: inputLanguage }));
		changeFlgHandler("params");
	};

	// обработчик изменения флага аудио-ответов
	const audioAnswersChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
		setCheckAudioAnswers(e.target.checked);
		dispatch(changeBooleanValuesWebConnector({ channel, field: "audioAnswers", value: e.target.checked }));
		changeFlgHandler("params");
	};

	// обработчик изменения флага отправки файлов
	const sendFileChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
		setCheckSendFile(e.target.checked);
		dispatch(changeBooleanValuesWebConnector({ channel, field: "sendFile", value: e.target.checked }));
		changeFlgHandler("params");
	};

	// обработчик изменения флага отправки аудио-сообщений
	const microphoneChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
		setCheckMicrophone(e.target.checked);
		dispatch(changeBooleanValuesWebConnector({ channel, field: "microphone", value: e.target.checked }));
		changeFlgHandler("params");
	};

	// обработчик изменения флага аудио-звонка
	const audioCallChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
		setCheckAudioCall(e.target.checked);
		dispatch(changeBooleanValuesWebConnector({ channel, field: "audioCall", value: e.target.checked }));
		changeFlgHandler("params");
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.container}>
				{/* общие настройки */}
				<H3 className={styles.header}>{translate("title_generalSettings")}</H3>

				<div className={styles.block}>
					{/* название коннектора */}
					<CustomTextField
						required
						fullWidth
						label={translate("input_connectorName")}
						value={inputConnectorName}
						onChange={(e) => setInputConnectorName(e.target.value)}
						onBlur={connectorNameChangeHandler}
						error={!inputConnectorName}
						disabled={isDisabled}
						hint="hint_connectorsTitle"
					/>
					{/* тип виджета */}
					<CustomTextField
						fullWidth
						required
						select
						label={translate("select_type")}
						value={selectWidgetType}
						onChange={widgetTypeChangeHandler}
						error={!selectWidgetType}
						disabled={isDisabled}
					>
						{WEB_CONNECTOR_TYPES_LIST.map(({ type, translation }) => (
							<CustomMenuItem key={type} value={type}>
								{translate(translation)}
							</CustomMenuItem>
						))}
					</CustomTextField>
				</div>

				<div className={styles.block}>
					{/* токен */}
					<CustomTextField
						required
						fullWidth
						label={translate("input_token")}
						value={inputToken}
						onChange={(e) => setInputToken(e.target.value)}
						onBlur={tokenChangeHandler}
						error={!inputToken}
						disabled={isDisabled}
						hint="hint_connectorsWebToken"
					/>
					{/* домен */}
					<CustomTextField
						required
						fullWidth
						label={translate("input_domain")}
						value={inputDomain}
						onChange={(e) => setInputDomain(e.target.value)}
						onBlur={domainChangeHandler}
						error={!inputDomain}
						disabled={isDisabled}
						hint="hint_connectorsWebDomain"
					/>
				</div>

				<div className={styles.block}>
					{/* заголовок */}
					<CustomTextField
						fullWidth
						label={translate("input_header")}
						value={inputHeader}
						onChange={(e) => setInputHeader(e.target.value)}
						onBlur={headerChangeHandler}
						disabled={isDisabled}
						hint="hint_connectorsWebHeader"
					/>
					{/* приветственное сообщение */}
					<CustomTextField
						fullWidth
						label={translate("input_welcomeMessage")}
						value={inputInitText}
						onChange={(e) => setInputInitText(e.target.value)}
						onBlur={initTextChangeHandler}
						disabled={isDisabled}
						hint="hint_connectorsWebWelcomeMessage"
					/>
				</div>

				<div className={styles.block}>
					{/* акцентный цвет */}
					<CustomTextField
						fullWidth
						label={translate("input_accentColor")}
						value={inputColor}
						onChange={(e) => setInputColor(e.target.value)}
						onBlur={colorChangeHandler}
						disabled={isDisabled}
						hint="hint_connectorsWebAccentColor"
					/>
					{/* язык */}
					<Autocomplete
						fullWidth
						autoHighlight
						options={translateList.data}
						value={inputLanguage}
						onChange={(_, value) => setInputLanguage(value || "")}
						onBlur={languageChangeHandler}
						noOptionsText={<NoOptionText />}
						disabled={isDisabled}
						renderInput={(params) => <CustomTextField {...params} label={translate("input_language")} />}
						renderOption={(props, option) => {
							return (
								<RenderOption {...props} key={option}>
									{option}
								</RenderOption>
							);
						}}
					/>
				</div>

				{settings.type === "chat" && (
					<div className={styles.containerInner}>
						{/* аудио-ответы */}
						<div className={styles.containerInnerField}>
							<CustomCheckbox
								label="checkbox_voiceAnswers"
								checked={checkAudioAnswers}
								onChange={audioAnswersChangeHandler}
								disabled={isDisabled}
								hint={"hint_connectorsWebAudioAnswers"}
								hintPosition="left"
							/>
						</div>
						{/* отправка файлов */}
						<div className={styles.containerInnerField}>
							<CustomCheckbox
								label="checkbox_sendingFiles"
								checked={checkSendFile}
								onChange={sendFileChangeHandler}
								disabled={isDisabled}
								hint={"hint_connectorsWebSendingFiles"}
								hintPosition="top"
							/>
						</div>
						{/* отправка аудио-сообщений */}
						<div className={styles.containerInnerField}>
							<CustomCheckbox
								label="checkbox_sendingAudioMessages"
								checked={checkMicrophone}
								onChange={microphoneChangeHandler}
								disabled={isDisabled}
								hint={"hint_connectorsWebSendingAudioMessages"}
								hintPosition="left"
							/>
						</div>
						{/* аудио-звонок */}
						<div className={styles.containerInnerField}>
							<CustomCheckbox
								label="checkbox_audioCall"
								checked={checkAudioCall}
								onChange={audioCallChangeHandler}
								disabled={isDisabled}
								hint={"hint_connectorsWebAudioCall"}
								hintPosition="bottom"
							/>
						</div>
					</div>
				)}

				{/* серверы */}
				<H3 className={styles.header}>{translate("title_servers")}</H3>
				<div className={styles.containerInner}>
					{settings.host.concat("").map((server, idx, array) => (
						<div className={styles.containerInnerField} key={server + idx}>
							<Server channel={channel} server={server} idx={idx} array={array} changeFlgHandler={changeFlgHandler} isDisabled={isDisabled} />
						</div>
					))}
				</div>

				{/* переменные */}
				{settings.type === "chat" && (
					<>
						<H3 className={styles.header}>{translate("title_variables")}</H3>
						{Object.entries(settings.variables)
							.concat([["", ""]])
							.map(([variable, value], idx, array) => (
								<FieldValueBlock
									key={idx}
									channel={channel}
									setting="variables"
									field={variable}
									fieldLabel="input_variable"
									value={value}
									valueLabel="input_value"
									idx={idx}
									array={array}
									changeFlgHandler={changeFlgHandler}
									isDisabled={isDisabled}
								/>
							))}
					</>
				)}
			</div>
		</Fade>
	);
};

export default Web;
